import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { CalendarItem } from '@/components/CalendarDrawer';
import type { PostIdea } from '@/components/chat/IdeaCards';
import { useSharedChatLogic } from '@/hooks/useSharedChatLogic';
import { sendChatMessage } from '@/utils/mainAppChatStreaming';
import { useWebsites } from '@/context/WebsitesContext';

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
  created_at?: string;
  updated_at?: string;
}

export interface ChatSession {
  id: string;
  website_id: string;
  title?: string;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

export interface WriteDrawerData {
  id?: string;
  subject_matter: string;
  post_content: string;
  post_digest?: string;
}

interface AppChatContextType {
  // Chat state
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  input: string;
  setInput: (val: string) => void;
  sending: boolean;
  setSending: (sending: boolean) => void;
  
  // Calendar state
  calendarItems: CalendarItem[];
  setCalendarItems: React.Dispatch<React.SetStateAction<CalendarItem[]>>;
  
  // Drawer state
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  writeDrawerOpen: boolean;
  setWriteDrawerOpen: (open: boolean) => void;
  writeDrawerData: WriteDrawerData | null;
  setWriteDrawerData: React.Dispatch<React.SetStateAction<WriteDrawerData | null>>;
  
  // Current session
  currentSession: ChatSession | null;
  setCurrentSession: React.Dispatch<React.SetStateAction<ChatSession | null>>;
  
  // Content editing state
  isEditingPost: boolean;
  setIsEditingPost: (editing: boolean) => void;
  editingPostData: {
    id: string;
    title: string;
    content: string;
    postType?: 'post_theme' | 'social';
    platform?: string;
  } | null;
  setEditingPostData: React.Dispatch<React.SetStateAction<{
    id: string;
    title: string;
    content: string;
    postType?: 'post_theme' | 'social';
    platform?: string;
  } | null>>;
  
  // Chat functions
  sendMessage: (message: string) => Promise<void>;
  handleCreateOutline: (idea: PostIdea) => void;
  generateDigest: (content: string, maxLength?: number) => string;
  
  // Session management
  createNewSession: (websiteId: string, title?: string) => Promise<ChatSession>;
  loadWebsiteContext: (websiteId: string) => Promise<void>;
  saveSession: () => Promise<void>;
  
  // Calendar functions
  addToCalendar: (item: Omit<CalendarItem, 'id'>) => void;
  removeFromCalendar: (id: string) => void;
  updateCalendarItem: (id: string, updates: Partial<CalendarItem>) => void;
  
  // Content editing functions
  startEditingPost: (postData: { id: string; title: string; content: string; postType?: 'post_theme' | 'social'; platform?: string }) => void;
  stopEditingPost: () => void;
  updatePostContent: (newContent: string) => void;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Scroll reference
  scrollRef: React.RefObject<HTMLDivElement>;
}

const AppChatContext = createContext<AppChatContextType | undefined>(undefined);

export const useAppChat = () => {
  const context = useContext(AppChatContext);
  if (context === undefined) {
    throw new Error('useAppChat must be used within an AppChatProvider');
  }
  return context;
};

interface AppChatProviderProps {
  children: React.ReactNode;
  websiteId?: string;
  currentWebsite?: { id: string; url: string };
}

export const AppChatProvider: React.FC<AppChatProviderProps> = ({ children, websiteId: propWebsiteId, currentWebsite: propCurrentWebsite }) => {
  // Get website info from context if not provided as props
  const { currentWebsite: contextWebsite } = useWebsites();
  const websiteId = propWebsiteId || contextWebsite?.id || '';
  const currentWebsite = propCurrentWebsite || contextWebsite;
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Shared chat logic (no language detection for main app chat)
  const { checkLanguageDetection, setupLanguageDetection, sendMessage: sharedSendMessage, handleCreateOutline: sharedHandleCreateOutline } = useSharedChatLogic({
    currentWebsite,
    websiteId,
    enableLanguageDetection: false
  });
  
  // Calendar state
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [writeDrawerOpen, setWriteDrawerOpen] = useState(false);
  const [writeDrawerData, setWriteDrawerData] = useState<WriteDrawerData | null>(null);
  
  // Content editing state
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editingPostData, setEditingPostData] = useState<{
    id: string;
    title: string;
    content: string;
    postType?: 'post_theme' | 'social';
    platform?: string;
  } | null>(null);
  
  // Session state
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for debouncing and scrolling
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastSavedMessagesRef = useRef<string>('');

  // Generate digest helper
  const generateDigest = useCallback((content: string, maxLength: number = 300): string => {
    if (!content) return '';
    
    // Remove HTML tags and get plain text
    const plainText = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    
    // If content is short enough, return as is
    if (plainText.length <= maxLength) return plainText;
    
    // Find the last complete sentence within the limit
    const truncated = plainText.substring(0, maxLength);
    const lastSentenceEnd = truncated.lastIndexOf('.');
    
    if (lastSentenceEnd > maxLength * 0.5) {
      return truncated.substring(0, lastSentenceEnd + 1);
    }
    
    // If no good sentence break, truncate at word boundary
    const lastSpace = truncated.lastIndexOf(' ');
    return truncated.substring(0, lastSpace) + '...';
  }, []);

  // Create new chat session using onboarding table
  const createNewSession = useCallback(async (websiteId: string, title?: string): Promise<ChatSession> => {
    try {
      setLoading(true);
      setError(null);

      const session: Omit<ChatSession, 'id' | 'created_at' | 'updated_at'> = {
        website_id: websiteId,
        title: title || 'New Chat',
        messages: []
      };

      const { data, error } = await supabase
        .from('onboarding')
        .upsert({
          website_id: websiteId,
          chat_summary: [],
          post_theme_content: null,
          post_theme_suggestions: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'website_id'
        })
        .select()
        .single();

      if (error) throw error;

      const newSession: ChatSession = {
        id: data.id,
        website_id: data.website_id,
        title: title || 'Chat Session',
        messages: Array.isArray(data.chat_summary) ? data.chat_summary : [],
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setCurrentSession(newSession);
      setMessages(newSession.messages);
      
      return newSession;
    } catch (error) {
      console.error('Error creating chat session:', error);
      setError(error instanceof Error ? error.message : 'Failed to create chat session');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load website context (no chat message hydration from DB to avoid overwrites)
  const loadWebsiteContext = useCallback(async (websiteId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Create a simple session object
      const session: ChatSession = {
        id: websiteId,
        website_id: websiteId,
        title: 'Chat Session',
        messages: messages,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setCurrentSession(session);
    } catch (error) {
      console.error('Error loading website context:', error);
      setError(error instanceof Error ? error.message : 'Failed to load website context');
    } finally {
      setLoading(false);
    }
  }, [messages]); // Add messages dependency to prevent infinite loops

  // Save current session to onboarding table (skip chat message persistence to avoid hydration races)
  const saveSession = useCallback(async (): Promise<void> => {
    if (!currentSession?.website_id) return;

    // Check if messages have actually changed since last save
    const messagesHash = JSON.stringify(messages);
    if (messagesHash === lastSavedMessagesRef.current) {
      return;
    }

    try {
      // Parse messages to extract ideas and content
      let parsedContentList = [];
      let parsedIdeasList = [];
      
      for (let index = 0; index < messages.length; index++) {
        const message = messages[index];
      
        if (!message?.content?.startsWith('{"_type"')) {
          continue;
        }
      
        const parsed = JSON.parse(message?.content);
      
        if (parsed && parsed?._type === 'post_draft') {
          parsedContentList.push(parsed);
        } else if (parsed?._type === 'ideas') {
          parsedIdeasList.push(parsed);
        }
      }

      const { error } = await supabase
        .from('onboarding')
        .update({
          post_theme_content: parsedContentList.length > 0 ? JSON.stringify(parsedContentList) : null,
          post_theme_suggestions: parsedIdeasList.length > 0 ? JSON.stringify(parsedIdeasList) : null,
          updated_at: new Date().toISOString()
        })
        .eq('website_id', currentSession.website_id);

      if (error) throw error;

      // Update ref to track last saved state
      lastSavedMessagesRef.current = messagesHash;
    } catch (error) {
      console.error('Error saving chat session:', error);
      setError(error instanceof Error ? error.message : 'Failed to save chat session');
    }
  }, [currentSession?.website_id, messages]);

  // Debounced save
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveSession();
    }, 2000);
  }, [saveSession]);

  // Auto-save when messages change (only trigger on messages change, not currentSession)
  useEffect(() => {
    if (messages.length > 0 && currentSession?.website_id) {
      debouncedSave();
    }
    
    // Cleanup: clear timeout when component unmounts or dependencies change
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [messages, debouncedSave]); // Removed currentSession from dependencies to prevent infinite loop

  // Load calendar items from localStorage (for chat-generated items only)
  const loadCalendarItems = useCallback(async () => {
    try {
      const storedItems = localStorage.getItem('calendar_items');
      if (storedItems) {
        const items = JSON.parse(storedItems);
        
        // Ensure all items have metadata for ContentTag display
        const itemsWithMetadata = items.map((item: CalendarItem) => ({
          ...item,
          format_template: item.format_template || 'guide',
          length_tag: item.length_tag || 'long',
          content_type: item.content_type || 'post',
          keywords: item.keywords || [],
          tone_style: item.tone_style || {},
          section_layout: item.section_layout || [],
          element_settings: item.element_settings || {}
        }));
        
        setCalendarItems(itemsWithMetadata);
        
        // Update localStorage with the enhanced items
        localStorage.setItem('calendar_items', JSON.stringify(itemsWithMetadata));
      }
    } catch (error) {
      console.error('Error loading calendar items from localStorage:', error);
    }
  }, []);

  // Load website context and calendar items on mount
  useEffect(() => {
    loadWebsiteContext(websiteId);
    loadCalendarItems();
  }, [websiteId, loadWebsiteContext, loadCalendarItems]);

  // Initial message and language detection
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hello! I'm here to help you with your content strategy. I can write blog posts and generate complete articles tailored to your website, create engaging social media posts for your channels, help you manage settings and configure your website preferences, plan content by suggesting topics and creating content calendars, and answer any questions about your content strategy and website. What would you like to work on today?"
      }]);
    }
  }, []);

  // Language detection is disabled for main app chat

  // Send message function for main app chat (uses database data)
  const sendMessage = useCallback(async (message: string): Promise<void> => {
    if (!message.trim() || sending) return;

    try {
      // Get website analysis data from database for main app chat
      const { data: websiteData } = await supabase
        .from('websites')
        .select('goal, offering, language')
        .eq('id', websiteId)
        .single();

      const websiteAnalysisData = {
        website_content: JSON.parse(localStorage.getItem('website_content') || '[]'),
        website_info: JSON.parse(localStorage.getItem('website_info') || '{}'),
        key_content_pages: JSON.parse(localStorage.getItem('key_content_pages') || '[]'),
        post_ideas: JSON.parse(localStorage.getItem('post_ideas') || '[]'),
        language: websiteData?.language || 'en',
        offering: websiteData?.offering || '',
        goal: websiteData?.goal || ''
      };

      // Use the shared streaming logic with database data
      await sendChatMessage({
        input: message,
        messages,
        currentWebsite,
        websiteAnalysisData,
        setMessages,
        setSending,
        setInput
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, I could not respond just now. Please try again.' }]);
      setSending(false);
    }
  }, [messages, sending, websiteId, currentWebsite]);

  // Handle create outline using shared logic
  const handleCreateOutline = useCallback(async (idea: PostIdea) => {
    await sharedHandleCreateOutline(idea, messages, setMessages, setInput, setSending);
  }, [messages, sharedHandleCreateOutline]);

  // Calendar functions - save to post_themes table and open sidebar
  const addToCalendar = useCallback(async (item: Omit<CalendarItem, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('post_themes')
        .insert({
          website_id: websiteId,
          subject_matter: item.title,
          post_digest: item.digest,
          post_content: item.fullContent,
          status: item.fullContent ? 'generated' : 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      const newItem: CalendarItem = {
        id: data.id,
        title: data.subject_matter,
        digest: data.post_digest,
        fullContent: data.post_content,
        isGenerated: !!data.post_content,
        isGenerating: false,
        postThemeId: data.id,
        format_template: data.format_template || 'guide',
        length_tag: data.length_tag || 'long',
        content_type: data.content_type || 'post',
        keywords: data.keywords || [],
        tone_style: data.tone_style || {},
        section_layout: data.section_layout || [],
        element_settings: data.element_settings || {}
      };

      // Update localStorage for chat context
      setCalendarItems(prev => {
        const updated = [newItem, ...prev];
        localStorage.setItem('calendar_items', JSON.stringify(updated));
        return updated;
      });
      
      // Open the calendar sidebar when item is added
      setDrawerOpen(true);
    } catch (error) {
      console.error('Error adding to calendar:', error);
    }
  }, [websiteId]);

  const removeFromCalendar = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('post_themes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setCalendarItems(prev => {
        const updated = prev.filter(item => item.id !== id);
        localStorage.setItem('calendar_items', JSON.stringify(updated));
        return updated;
      });
    } catch (error) {
      console.error('Error removing from calendar:', error);
    }
  }, []);

  const updateCalendarItem = useCallback(async (id: string, updates: Partial<CalendarItem>) => {
    try {
      const { error } = await supabase
        .from('post_themes')
        .update({
          subject_matter: updates.title,
          post_digest: updates.digest,
          post_content: updates.fullContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setCalendarItems(prev => {
        const updated = prev.map(item => 
          item.id === id ? { ...item, ...updates } : item
        );
        localStorage.setItem('calendar_items', JSON.stringify(updated));
        return updated;
      });
    } catch (error) {
      console.error('Error updating calendar item:', error);
    }
  }, []);

  // Content editing functions
  const startEditingPost = useCallback((postData: { id: string; title: string; content: string; postType?: 'post_theme' | 'social'; platform?: string }) => {
    console.log('[AppChatContext] Starting to edit post:', postData);
    setIsEditingPost(true);
    setEditingPostData(postData);
  }, []);

  const stopEditingPost = useCallback(() => {
    setIsEditingPost(false);
    setEditingPostData(null);
  }, []);

  const updatePostContent = useCallback((newContent: string) => {
    if (editingPostData) {
      setEditingPostData(prev => prev ? { ...prev, content: newContent } : null);
    }
  }, [editingPostData]);

  const value: AppChatContextType = {
    // Chat state
    messages,
    setMessages,
    input,
    setInput,
    sending,
    setSending,
    
    // Calendar state
    calendarItems,
    setCalendarItems,
    
    // Drawer state
    drawerOpen,
    setDrawerOpen,
    writeDrawerOpen,
    setWriteDrawerOpen,
    writeDrawerData,
    setWriteDrawerData,
    
    // Session state
    currentSession,
    setCurrentSession,
    
    // Content editing state
    isEditingPost,
    setIsEditingPost,
    editingPostData,
    setEditingPostData,
    
    // Chat functions
    sendMessage,
    handleCreateOutline,
    generateDigest,
    
    // Session management
    createNewSession,
    loadWebsiteContext,
    saveSession,
    
    // Calendar functions
    addToCalendar,
    removeFromCalendar,
    updateCalendarItem,
    
    // Content editing functions
    startEditingPost,
    stopEditingPost,
    updatePostContent,
    
    // Loading states
    loading,
    error,
    
    // Scroll reference
    scrollRef
  };

  return (
    <AppChatContext.Provider value={value}>
      {children}
    </AppChatContext.Provider>
  );
};
